import type { TaskKind, TaskView } from '../../api/types'

/**
 * How each kind of work is labelled and coloured.
 *
 * A bug is red and named; a task is the unremarkable default and carries no
 * badge at all, because marking the ordinary case only adds noise to a board
 * where most cards are tasks.
 */
export const TASK_TYPES: Record<TaskKind, { label: string; colour: string }> = {
  task: { label: 'Task', colour: 'gray' },
  bug: { label: 'Bug', colour: 'red' },
}

/** P1 shouts, P4 is silent. Anything more than this is decoration. */
export const PRIORITY_COLOURS: Record<number, string> = {
  1: 'red',
  2: 'orange',
  3: 'blue',
  4: 'gray',
}

export function dueLabel(task: TaskView): string | null {
  if (!task.due_on) return null
  if (task.due_today) return 'Today'

  const due = new Date(`${task.due_on}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000)

  if (days === 1) return 'Tomorrow'
  if (days === -1) return 'Yesterday'
  if (days < 0) return `${Math.abs(days)} days ago`
  if (days < 7) return due.toLocaleDateString(undefined, { weekday: 'long' })

  return due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export function dueColour(task: TaskView): string {
  if (task.overdue) return 'red'
  if (task.due_today) return 'orange'
  return 'dimmed'
}

/** Drag payloads, so drop handlers can tell what they are looking at. */
export type TaskDragData = {
  kind: 'task'
  taskId: string
  sectionId: string | null
}

export type ColumnDropData = {
  kind: 'column'
  sectionId: string | null
}

/**
 * A column being dragged to a new place in the board.
 *
 * A separate kind from a task, because a column and a card dropped on the
 * same target mean two different things - one lands in the column, the other
 * lands beside it.
 */
export type SectionDragData = {
  kind: 'section'
  sectionId: string
}

export function isTaskDrag(data: Record<string | symbol, unknown>): data is TaskDragData {
  return data.kind === 'task' && typeof data.taskId === 'string'
}

export function isColumnDrop(data: Record<string | symbol, unknown>): data is ColumnDropData {
  return data.kind === 'column'
}

export function isSectionDrag(data: Record<string | symbol, unknown>): data is SectionDragData {
  return data.kind === 'section' && typeof data.sectionId === 'string'
}

/**
 * The archive slot, which is a drop target and not a column.
 *
 * A task dropped here keeps the column it was in, so putting it back returns
 * it where it was rather than to wherever the board happens to start.
 */
export function isArchiveDrop(data: Record<string | symbol, unknown>): boolean {
  return data.kind === 'archive'
}

/**
 * How long a task took, from being created to being ticked off.
 *
 * Computed rather than stored: both timestamps are already on the task, so a
 * third field would be a second copy of an answer that can be derived - and
 * the copy is the one that would be wrong after a task was reopened.
 *
 * It measures creation to completion, which is what somebody means by "how
 * long did this take" on a board: the clock starts when the work was written
 * down. A task reopened and finished again reports from its original
 * creation, because that is still when it was asked for.
 *
 * Null when it is not finished, or when the timestamps make no sense
 * together - an imported task completed before it was created should report
 * nothing rather than a negative duration.
 */
export function completionDuration(task: TaskView): string | null {
  if (!task.completed || !task.completed_at) return null

  const ms = new Date(task.completed_at).getTime() - new Date(task.created_at).getTime()

  if (!Number.isFinite(ms) || ms < 0) return null

  const minutes = Math.floor(ms / 60_000)

  if (minutes < 1) return 'under a minute'
  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)

  // Under a day, the odd minutes still matter: "3h" and "3h 50m" are a
  // different afternoon.
  if (hours < 24) {
    const rest = minutes % 60

    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
  }

  const days = Math.floor(hours / 24)
  const rest = hours % 24

  // Past a day, hours are noise: nobody reports a fortnight to the hour.
  if (days >= 7 || rest === 0) return `${days}d`

  return `${days}d ${rest}h`
}
